import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDT } from "@/constants";

export default function SeoParams() {
  const [status, setStatus] = useState(null);
  const [sites, setSites] = useState(null);
  const [cid, setCid] = useState("");
  const [csecret, setCsecret] = useState("");
  const [editCreds, setEditCreds] = useState(false);
  const [maxMin, setMaxMin] = useState("25");
  const [syncing, setSyncing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [biz, setBiz] = useState(null);
  const [bizText, setBizText] = useState("");
  const load = useCallback(() => {
    api.get("/seo/gsc/status").then((r) => setStatus(r.data));
    api.get("/settings").then((r) => setMaxMin(String(r.data.seo_max_minutes ?? 25)));
    api.get("/seo/biz-info").then((r) => { setBiz(r.data); setBizText(r.data.biz_info || ""); })
      .catch(() => setBiz({ error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const saveCreds = async () => {
    try {
      await api.post("/seo/gsc/credentials", { client_id: cid, client_secret: csecret });
      setCsecret("");
      setEditCreds(false);
      toast.success("Identifiants Google enregistrés (chiffrés côté serveur)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible — réessayez");
    }
  };
  const listSites = async () => {
    try {
      const r = await api.get("/seo/gsc/sites");
      setSites(r.data.sites);
      if (!r.data.sites.length) toast.info("Aucune propriété trouvée sur ce compte Google — vérifiez que ce compte a bien accès à la propriété photobooth54.fr dans Search Console");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lecture des propriétés impossible — reconnectez Search Console", { duration: 12000 });
    }
  };
  const disconnect = async () => {
    try {
      await api.post("/seo/gsc/disconnect");
      setSites(null);
      toast.success("Google Search Console déconnecté (autorisation révoquée)");
    } catch {
      toast.error("Déconnexion impossible — réessayez");
    }
    load();
  };
  const saveMaxMin = async (v) => {
    setMaxMin(v);
    try {
      await api.put("/settings", { seo_max_minutes: parseInt(v) });
      toast.success("Temps de trajet SEO enregistré — pensez à recalculer les communes (onglet Référencement local)");
    } catch {
      toast.error("Enregistrement impossible — réessayez");
    }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/seo/gsc/sync");
      toast.success("Données Search Console synchronisées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synchronisation impossible pour le moment");
    }
    setSyncing(false);
    load();
  };
  const connect = () => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/seo/gsc/oauth/start`; };
  const saveBiz = async () => {
    try {
      const r = await api.put("/seo/biz-info", { biz_info: bizText });
      setBiz(r.data);
      toast.success(r.data.conflicts?.length
        ? "Enregistré — mais une contradiction avec vos paramètres est signalée ci-dessous"
        : "Infos commerciales SEO enregistrées");
    } catch {
      toast.error("Enregistrement impossible — réessayez");
    }
  };
  if (!status) return <p className="text-sm text-slate-400">Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="seo-params">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="seo-gsc-card">
        <p className="font-heading font-bold">Google Search Console</p>
        {status.connected ? (
          <div className="space-y-2 text-sm">
            <p className="text-emerald-700 font-semibold" data-testid="seo-gsc-connected">✓ Compte Google connecté (lecture seule)</p>
            {status.property && <p data-testid="seo-gsc-property">Propriété : <strong className="break-all">{status.property}</strong></p>}
            <p className="text-xs text-slate-500">Dernière synchronisation : <strong data-testid="seo-gsc-last-sync">{status.last_sync ? fmtDT(status.last_sync) : "jamais"}</strong>
              {status.last_sync_ok === false && <span className="text-rose-600 font-semibold"> — en échec : {status.last_error}</span>}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <button data-testid="seo-gsc-sync" onClick={sync} disabled={syncing}
                className="rounded-xl bg-sky-600 text-white font-bold text-sm py-2.5 disabled:opacity-50">
                {syncing ? "Synchronisation…" : "Actualiser les données"}
              </button>
              <button data-testid="seo-gsc-sites" onClick={listSites}
                className="rounded-xl border border-slate-300 font-bold text-sm py-2.5 text-slate-700">
                Choisir la propriété
              </button>
              <button data-testid="seo-gsc-disconnect" onClick={disconnect}
                className="rounded-xl border border-rose-300 text-rose-700 font-bold text-sm py-2.5">
                Déconnecter Google Search Console
              </button>
            </div>
            {sites && sites.length > 0 && (
              <div className="space-y-1.5 pt-1" data-testid="seo-gsc-sites-list">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Propriétés accessibles :</p>
                {sites.map((s) => (
                  <button key={s.siteUrl} data-testid={`seo-gsc-site-${s.siteUrl}`} onClick={async () => { await api.post("/seo/gsc/property", { site_url: s.siteUrl }); toast.success("Propriété sélectionnée"); load(); }}
                    className={`w-full text-left rounded-lg border px-3 py-2 text-sm font-medium break-all ${status.property === s.siteUrl ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-sky-300"}`}>
                    {s.siteUrl} <span className="text-xs text-slate-400">({s.permissionLevel})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {status.configured ? (
              <>
                <p className="text-emerald-700 font-semibold" data-testid="seo-gsc-configured">✓ Identifiants Google enregistrés</p>
                {!editCreds ? (
                  <>
                    <button data-testid="seo-gsc-connect" onClick={connect}
                      className="w-full rounded-xl bg-slate-900 text-white font-extrabold text-base py-3">
                      🔗 Connecter Google Search Console
                    </button>
                    <button data-testid="seo-gsc-edit-creds" onClick={() => { setCid(status.client_id || ""); setCsecret(""); setEditCreds(true); }}
                      className="w-full rounded-xl border border-slate-300 text-slate-700 font-bold text-sm py-2.5">
                      Modifier les identifiants Google
                    </button>
                  </>
                ) : (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="seo-gsc-edit-form">
                    <p className="text-xs font-semibold text-slate-600">Remplacer les identifiants (l'ancien Client Secret n'est jamais affiché) :</p>
                    <input data-testid="seo-gsc-client-id" value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Client ID (…apps.googleusercontent.com)"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white" />
                    <input data-testid="seo-gsc-client-secret" type="password" value={csecret} onChange={(e) => setCsecret(e.target.value)} placeholder="Nouveau Client Secret"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white" />
                    <div className="grid grid-cols-2 gap-2">
                      <button data-testid="seo-gsc-save-creds" onClick={saveCreds} disabled={!cid || !csecret}
                        className="rounded-lg bg-slate-900 text-white font-bold text-sm py-2.5 disabled:opacity-50">
                        Enregistrer
                      </button>
                      <button data-testid="seo-gsc-cancel-edit" onClick={() => { setEditCreds(false); setCsecret(""); }}
                        className="rounded-lg border border-slate-300 text-slate-600 font-bold text-sm py-2.5">
                        Annuler
                      </button>
                    </div>
                    <p className="text-[11px] text-slate-400">Les nouvelles valeurs remplacent les anciennes (stockage chiffré). Les autres réglages et données ne sont pas touchés.</p>
                  </div>
                )}
                <p className="text-xs text-slate-400">Vous serez redirigé vers Google (autorisation officielle, lecture seule). Votre mot de passe Google n'est jamais demandé dans l'application.</p>
              </>
            ) : (
              <>
                <p className="text-slate-600">Pour récupérer les vraies statistiques de photobooth54.fr, enregistrez vos identifiants OAuth Google (créés dans Google Cloud Console) :</p>
                <input data-testid="seo-gsc-client-id" value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Client ID (…apps.googleusercontent.com)"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <input data-testid="seo-gsc-client-secret" type="password" value={csecret} onChange={(e) => setCsecret(e.target.value)} placeholder="Client Secret"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm" />
                <button data-testid="seo-gsc-save-creds" onClick={saveCreds} disabled={!cid || !csecret}
                  className="w-full rounded-xl bg-slate-900 text-white font-bold text-sm py-2.5 disabled:opacity-50">
                  Enregistrer les identifiants (stockage chiffré)
                </button>
                <button data-testid="seo-gsc-guide" onClick={() => setGuideOpen(!guideOpen)}
                  className="w-full rounded-xl border border-sky-300 text-sky-700 font-bold text-sm py-2.5">
                  {guideOpen ? "Masquer le guide" : "Guide : créer mes identifiants Google (pas à pas)"}
                </button>
                {guideOpen && (
                  <ol className="text-xs text-slate-600 space-y-1.5 list-decimal pl-4" data-testid="seo-gsc-guide-steps">
                    <li>Ouvrez <strong>console.cloud.google.com</strong> avec votre compte Google et créez un projet (ex. « Photobooth54 SEO »).</li>
                    <li>Menu <strong>APIs et services → Bibliothèque</strong> : recherchez et activez <strong>Google Search Console API</strong>.</li>
                    <li>Menu <strong>Google Auth Platform → Branding</strong> : nom de l'application, e-mail d'assistance. Ajoutez votre adresse Gmail dans <strong>Utilisateurs test</strong>.</li>
                    <li>Menu <strong>Clients → Créer un client</strong> : type <strong>Application Web</strong>.</li>
                    <li>Dans <strong>URI de redirection autorisés</strong>, ajoutez exactement :<br />
                      <code className="block bg-slate-100 rounded px-2 py-1 mt-1 break-all select-all">{process.env.REACT_APP_BACKEND_URL}/api/seo/gsc/oauth/callback</code>
                      <span className="text-slate-400">(ajoutez aussi l'URI de votre domaine de production après déploiement)</span></li>
                    <li>Copiez le <strong>Client ID</strong> et le <strong>Client Secret</strong> dans les champs ci-dessus, puis enregistrez.</li>
                    <li>Cliquez ensuite sur « Connecter Google Search Console » et choisissez la propriété <strong>photobooth54.fr</strong>.</li>
                  </ol>
                )}
              </>
            )}
          </div>
        )}
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="seo-zone-params">
        <p className="font-heading font-bold">Zone géographique SEO</p>
        <p className="text-sm text-slate-600">Ville de départ : <strong>Lesménils (54700)</strong> — fixe.</p>
        <div className="space-y-1.5">
          <label className="text-sm text-slate-600">Temps de trajet SEO prioritaire (zone « communes proches »)</label>
          <select data-testid="seo-max-minutes" value={maxMin} onChange={(e) => saveMaxMin(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm w-full sm:w-48 bg-white">
            {["15", "20", "25", "30"].map((v) => <option key={v} value={v}>{v} minutes</option>)}
          </select>
        </div>
        <p className="text-xs text-slate-400">Les zones Metz et Nancy sont conservées séparément, même au-delà de ce temps de trajet. Ce réglage SEO ne modifie ni les zones de livraison, ni les suppléments kilométriques, ni les règles saisonnières.</p>
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="seo-biz-card">
        <p className="font-heading font-bold">Infos commerciales utilisées par l'Assistant SEO</p>
        {biz && !biz.error && (
          <>
            <div className="text-xs text-slate-600 space-y-1" data-testid="seo-biz-facts">
              <p className="font-semibold text-slate-500 uppercase tracking-wider text-[11px]">Reprises automatiquement de vos paramètres (non modifiables ici) :</p>
              <p>📍 Basé à <strong>{biz.ville}</strong>, zone {biz.zone}</p>
              {biz.prix_min != null && <p>💶 Formules dès <strong>{biz.prix_min} €</strong></p>}
              <p>🚗 Livraison incluse à moins de <strong>{biz.km_inclus} km</strong> de Lesménils — au-delà, supplément de {String(biz.tarif_km).replace(".", ",")} €/km</p>
              <p>🎙 Audio Guest Book : {biz.agb_price} € · Cabine : {biz.cabine_price} €</p>
            </div>
            {biz.conflicts?.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="seo-biz-conflicts">
                <p className="text-xs font-bold text-amber-900">⚠ Contradiction à clarifier — le texte ci-dessous ne sera pas utilisé tel quel dans les propositions :</p>
                {biz.conflicts.map((c, i) => <p key={i} className="text-xs text-amber-800">{c}</p>)}
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-sm text-slate-600">Compléments libres (installation, options, précisions commerciales) — utilisés uniquement s'ils ne contredisent pas vos paramètres</label>
              <textarea data-testid="seo-biz-info-input" value={bizText} onChange={(e) => setBizText(e.target.value)} rows={3}
                placeholder="Ex. : installation et accessoires inclus dans chaque formule, livre d'or offert…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white" />
              <button data-testid="seo-biz-info-save" onClick={saveBiz}
                className="rounded-xl bg-slate-900 text-white font-bold text-sm px-4 py-2.5">
                Enregistrer
              </button>
            </div>
          </>
        )}
        {biz?.error && <p className="text-sm text-rose-600" data-testid="seo-biz-error">Lecture impossible — réessayez en rechargeant la page.</p>}
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2" data-testid="seo-sync-info">
        <p className="font-heading font-bold">Synchronisation automatique</p>
        <p className="text-sm text-slate-600">Les données Search Console sont synchronisées automatiquement chaque matin (6h UTC), uniquement si la connexion est active. Une erreur de synchronisation n'impacte jamais le reste de l'application.</p>
      </section>
    </div>
  );
}
